import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { Plugs } from "@phosphor-icons/react";
import XeroCard from "./XeroCard";
import GoCardlessCard from "./GoCardlessCard";
import QuickBooksCard from "./QuickBooksCard";
import BooksMoreCard, { BOOKS_MORE } from "./BooksMoreCard";
import TwilioCard from "./TwilioCard";
import ZapierCard from "./ZapierCard";
import CalendarCard from "./CalendarCard";
import IcalCard from "./IcalCard";
import SyncLog from "./SyncLog";
import AppCredentials from "./AppCredentials";

export const PROVIDER_LABELS = { xero: "Xero", quickbooks: "QuickBooks Online", freeagent: "FreeAgent", sage: "Sage Accounting", zoho_books: "Zoho Books", freshbooks: "FreshBooks", gocardless: "GoCardless", google_calendar: "Google Calendar", microsoft_calendar: "Outlook / Microsoft 365", ical: "Calendar feed", twilio: "SMS (Twilio)", zapier: "Zapier" };

export default function IntegrationsHub() {
  const [data, setData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/integrations");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load integrations");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = searchParams.get("integration");
    if (!p) return;
    const name = PROVIDER_LABELS[p] || p;
    if (searchParams.get("connected")) toast.success(`${name} connected`);
    else if (searchParams.get("error")) toast.error(`${name}: ${searchParams.get("error")}`);
    const next = new URLSearchParams(searchParams);
    ["integration", "connected", "error"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!data) return <div className="text-sm text-muted-foreground p-4" data-testid="integrations-loading">Loading integrations…</div>;
  const by = Object.fromEntries(data.providers.map((p) => [p.provider, p]));

  return (
    <div className="space-y-6" data-testid="integrations-hub">
      <div>
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2"><Plugs size={18} weight="duotone" /> Integrations</h2>
        <p className="text-xs text-muted-foreground mt-1">Connect the tools you already use. Everything here is self-serve — connect, pick your options, and watch the sync log.</p>
      </div>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Accounting & payments</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <XeroCard item={by.xero} isAdmin={data.is_admin} onChange={load} />
          <QuickBooksCard item={by.quickbooks} isAdmin={data.is_admin} onChange={load} />
          {Object.keys(BOOKS_MORE).map((p) => <BooksMoreCard key={p} provider={p} item={by[p]} isAdmin={data.is_admin} onChange={load} />)}
          <GoCardlessCard item={by.gocardless} isAdmin={data.is_admin} onChange={load} />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Messaging & automation</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <TwilioCard item={by.twilio} isAdmin={data.is_admin} onChange={load} />
          <ZapierCard item={by.zapier} isAdmin={data.is_admin} />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your calendar</h3>
        <div className="grid gap-4 lg:grid-cols-3">
          <CalendarCard item={by.google_calendar} onChange={load} />
          <CalendarCard item={by.microsoft_calendar} onChange={load} />
          <IcalCard item={by.ical} onChange={load} />
        </div>
      </section>

      <SyncLog />

      {data.is_admin && <AppCredentials endpoint="/integrations/apps" variant="tenant" onChange={load} />}
    </div>
  );
}
